import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, ShieldCheck, AlertTriangle, FileText, Phone, Mail, MapPin,
  Briefcase, Languages, GraduationCap, Star, CalendarDays, DollarSign, XCircle,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function fileUrl(url) {
  if (!url) return "#";
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
}

function statusClass(status) {
  if (status === "approved") return "bg-green-100 text-green-700";
  if (status === "rejected") return "bg-red-100 text-red-700";
  return "bg-amber-100 text-amber-700";
}

function initials(pro) {
  return `${pro?.first_name?.[0] || ""}${pro?.last_name?.[0] || ""}` || "NS";
}

export default function DispatcherProfessionalDetail() {
  const { id } = useParams();
  const [pro, setPro] = useState(null);
  const [checks, setChecks] = useState({});

  const load = async () => {
    const { data } = await api.get(`/professionals/${id}`);
    setPro(data);
  };

  useEffect(() => { load(); }, [id]);

  const setCheck = (credentialId, key, value) => {
    setChecks((prev) => ({
      ...prev,
      [credentialId]: {
        ...(prev[credentialId] || {}),
        [key]: value,
      },
    }));
  };

  const reviewCredential = async (credentialId, status) => {
    const verification_checks = checks[credentialId] || {};

    if (status === "approved") {
      if (!pro.resume_url) {
        toast.error("Resume must be uploaded before approving credentials.");
        return;
      }

      const required = [
        "license_number_cross_checked",
        "government_registry_verified",
        "license_active",
        "expiry_date_verified",
        "name_matches_profile",
      ];

      const missing = required.filter((k) => !verification_checks[k]);
      if (missing.length > 0) {
        toast.error("Complete all license verification checklist items before approval.");
        return;
      }
    }

    let rejection_reason = null;
    if (status === "rejected") {
      rejection_reason = window.prompt("Reason for rejection:");
      if (!rejection_reason) return;
    }

    const verification_notes = window.prompt("Verification notes, optional:") || "";

    try {
      const { data } = await api.patch(`/professionals/${pro.id}/credentials/${credentialId}/review`, {
        status,
        rejection_reason,
        verification_checks,
        verification_notes,
      });
      setPro(data);
      toast.success(`Credential ${status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Review failed");
    }
  };

  if (!pro) return <div className="text-slate-500">Loading professional profile…</div>;

  const verificationStatus = pro.verification_status || "pending_documents";
  const approved = verificationStatus === "approved";

  return (
    <div className="space-y-6" data-testid="dispatcher-professional-detail">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/dispatch/professionals" className="inline-flex items-center gap-2 text-sm text-brand-navy hover:underline">
          <ArrowLeft className="w-4 h-4" /> Back to Professionals
        </Link>
        <span className={`status-pill ${statusClass(approved ? "approved" : verificationStatus === "rejected" ? "rejected" : "pending")}`}>
          Verification: {verificationStatus.replaceAll("_", " ")}
        </span>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="shrink-0">
              {pro.avatar_url ? (
                <img src={fileUrl(pro.avatar_url)} alt={`${pro.first_name} ${pro.last_name}`} className="w-32 h-32 rounded-2xl object-cover border border-slate-200" />
              ) : (
                <div className="w-32 h-32 rounded-2xl bg-brand-navy text-white flex items-center justify-center text-4xl font-bold">
                  {initials(pro)}
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-3xl font-bold text-brand-navy" style={{ fontFamily: "Outfit, sans-serif" }}>
                {pro.first_name} {pro.last_name}
              </h1>
              <div className="text-slate-500 mt-1">{pro.primary_position}</div>

              <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm text-slate-700">
                <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-brand-teal" /> {pro.email}</div>
                <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-brand-teal" /> {pro.phone}</div>
                <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-brand-teal" /> {pro.city}, {pro.province}</div>
                <div className="flex items-center gap-2"><Briefcase className="w-4 h-4 text-brand-teal" /> {pro.experience_years} years experience</div>
                <div className="flex items-center gap-2"><Star className="w-4 h-4 text-amber-500" /> Score {pro.netstaff_score || 0}</div>
                <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" /> Incidents {pro.incident_count || 0}</div>
              </div>

              {pro.bio && <p className="mt-5 text-sm text-slate-600 leading-6">{pro.bio}</p>}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-5 space-y-4">
            <h2 className="font-bold text-slate-900 flex items-center gap-2"><DollarSign className="w-4 h-4 text-brand-teal" /> Rates</h2>
            <div className="text-sm text-slate-700 space-y-2">
              <div>Regular: <strong>${pro.hourly_rate || 0}/hr</strong></div>
              <div>Weekend: <strong>${pro.weekend_rate || 0}/hr</strong></div>
              <div>Holiday: <strong>${pro.holiday_rate || 0}/hr</strong></div>
              <div>Overtime: <strong>${pro.overtime_rate || 0}/hr</strong></div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 space-y-4">
            <h2 className="font-bold text-slate-900 flex items-center gap-2"><CalendarDays className="w-4 h-4 text-brand-teal" /> Availability</h2>
            <div className="text-sm text-slate-700 space-y-2">
              <div>Available now: <strong>{pro.available_now ? "Yes" : "No"}</strong></div>
              <div>Days: {(pro.available_days || []).join(", ") || "—"}</div>
              <div>Shifts: {(pro.shift_preferences || []).join(", ") || "—"}</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 space-y-4">
            <h2 className="font-bold text-slate-900 flex items-center gap-2"><FileText className="w-4 h-4 text-brand-teal" /> Resume</h2>
            {pro.resume_url ? (
              <a href={fileUrl(pro.resume_url)} target="_blank" rel="noreferrer">
                <Button className="bg-brand-navy hover:bg-brand-blue w-full">
                  View / Download Resume
                </Button>
              </a>
            ) : (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                Resume missing. Do not approve credentials until resume is uploaded.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="p-5 space-y-4">
            <h2 className="font-bold text-slate-900 flex items-center gap-2"><GraduationCap className="w-4 h-4 text-brand-teal" /> Skills & Background</h2>
            <div className="text-sm text-slate-700 space-y-3">
              <div><strong>Education:</strong> {pro.education || "—"}</div>
              <div><strong>Secondary Positions:</strong> {(pro.secondary_positions || []).join(", ") || "—"}</div>
              <div><strong>Specialties:</strong> {(pro.specialties || []).join(", ") || "—"}</div>
              <div><strong>Skills:</strong> {(pro.skills || []).join(", ") || "—"}</div>
              <div className="flex items-center gap-2"><Languages className="w-4 h-4" /> {(pro.languages || []).join(", ") || "—"}</div>
            </div>
          </CardContent>
        </Card>

        <Card className={approved ? "border-green-200" : "border-amber-200"}>
          <CardContent className="p-5 space-y-3">
            <h2 className="font-bold text-slate-900 flex items-center gap-2">
              {approved ? <ShieldCheck className="w-4 h-4 text-green-600" /> : <AlertTriangle className="w-4 h-4 text-amber-600" />}
              Verification Summary
            </h2>
            <div className="text-sm text-slate-700">
              Current status: <strong>{verificationStatus.replaceAll("_", " ")}</strong>
            </div>
            <div className="text-sm text-slate-600">
              Approval requires uploaded resume, credential document review, and completed license verification checklist.
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-5">
          <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-brand-teal" /> Credentials & License Review
          </h2>

          {(pro.credentials || []).length === 0 && (
            <div className="text-sm text-slate-500">No credentials uploaded.</div>
          )}

          <div className="space-y-4">
            {(pro.credentials || []).map((c) => (
              <div key={c.id} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-semibold text-slate-900">{c.type}</div>
                    <div className="text-xs text-slate-500">
                      {c.expiry ? `Expires ${c.expiry}` : "No expiry"} · Uploaded {c.uploaded_at ? c.uploaded_at.slice(0, 10) : "—"}
                    </div>
                    {c.rejection_reason && (
                      <div className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <XCircle className="w-3 h-3" /> {c.rejection_reason}
                      </div>
                    )}
                    {c.verification_notes && (
                      <div className="text-xs text-slate-500 mt-1">Notes: {c.verification_notes}</div>
                    )}
                  </div>
                  <span className={`status-pill ${statusClass(c.status || "pending")}`}>{c.status || "pending"}</span>
                </div>

                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  {c.file_url && (
                    <a href={fileUrl(c.file_url)} target="_blank" rel="noreferrer" className="text-sm text-brand-navy hover:underline flex items-center gap-1">
                      <FileText className="w-4 h-4" /> View Credential File
                    </a>
                  )}
                </div>

                {(c.status || "pending") === "pending" && (
                  <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
                    <div className="text-sm font-semibold text-amber-800 mb-3">
                      Dispatcher License Verification Checklist
                    </div>

                    <div className="grid md:grid-cols-2 gap-2 text-sm text-slate-700">
                      {[
                        ["license_number_cross_checked", "License number cross-checked on official government / regulator website"],
                        ["government_registry_verified", "Professional found on official government / regulatory registry"],
                        ["license_active", "License is active and in good standing"],
                        ["expiry_date_verified", "Expiry date is accurate and current"],
                        ["name_matches_profile", "Registered name matches NetStaff profile"],
                      ].map(([key, label]) => (
                        <label key={key} className="flex items-start gap-2">
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={Boolean((checks[c.id] || {})[key])}
                            onChange={(e) => setCheck(c.id, key, e.target.checked)}
                          />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>

                    <div className="mt-4 flex items-center gap-2 flex-wrap">
                      <Button className="bg-green-600 hover:bg-green-700" onClick={() => reviewCredential(c.id, "approved")}>
                        Approve Credential
                      </Button>
                      <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => reviewCredential(c.id, "rejected")}>
                        Reject Credential
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
